import { Card, Flex, Row, Col, Grid, Space, Input, message, Popconfirm, Button, Pagination } from "antd";
import {
  UpCircleOutlined,
  DownCircleOutlined,
  FlagOutlined,
  FlagFilled,
  QuestionCircleOutlined
} from '@ant-design/icons';
import { useFlowStore } from '../store';
import { useEffect, useState, useMemo, useCallback } from "react";
import React from 'react'
import { parseISO, format, differenceInMilliseconds, addMinutes, isBefore } from 'date-fns';
import { useParams, useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
// import { NodeData } from "../types";
import {
  NodeData,
  LinkData,
  UserIdentifier
} from '../../api/api';
import AuthenticatedLayout from '../layout/AuthenticatedLayout';
import { debounce } from 'lodash';
import { KeyAdmin, KeyReputationEditor } from '../constants';
import VideoCard from './VideoCard';
import PlayerPage from "./PlayerPage";

const WikiPage = () => {
  const navigate = useNavigate();
  const [videos, setVideos] = useState<LinkData[]>([]);

  // 2. Debounce input changes
  const debouncedSetDescription = useMemo(
    () => debounce((value: string) => {
      setDescription(value);
    }, 300), // Increased from 150ms to 300ms
    []
  );

  const debouncedSetTitle = useMemo(
    () => debounce((value: string) => {
      setTitle(value);
    }, 300), // Increased from 150ms to 300ms
    []
  );

  // 3. Optimize input handlers
  const handleDescriptionChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    debouncedSetDescription(value);
  }, [debouncedSetDescription]);

  const handleTitleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    debouncedSetTitle(value);
  }, [debouncedSetTitle]);

  const { topic } = useParams<{ topic: string }>();
  const { nid } = useParams<{ nid: string }>();
  const { getNode, selectedNode, deleteNode, putNodeTitle, putNodeVideoVote, putNodeVideoEdit, putNodeBattleVote, putNodeFlag, putNodeFreshVote, clearError, userDetails, getUser, getNextNodeBattleTested, getNextNodeFresh, lastMap} = useFlowStore();

  useEffect(() => {
    if (userDetails?.id) {
      getUser(userDetails.id);
    }
  }, [userDetails?.id, getUser]);

  const screens = Grid.useBreakpoint();

  const [isLoading, setIsLoading] = useState(true);
  const [localTitle, setTitle] = useState('');
  const [localDescription, setDescription] = useState('');
  const [localIsFlagged, setIsFlagged] = useState(false);
  const [localCreatedBy, setCreatedBy] = useState<UserIdentifier>({ id: "", username: "" });
  const [localEditedBy, setEditedBy] = useState<UserIdentifier[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [editTime, setEditTime] = useState("");
  const [isEditable, setIsEditable] = useState(true);
  const [messageApi, contextHolder] = message.useMessage();

  // Add this useEffect to clean up state when component unmounts
  useEffect(() => {
    // This function runs when the component unmounts or before re-running the effect
    return () => {
      // Clear all state
      setVideos([]);
      setTitle('');
      setDescription('');
      setIsFlagged(false);
      setCreatedBy({ id: "", username: "" });
      setEditedBy([]);
      setIsEditing(false);
      setInputValue('');
      setEditTime("");
      setIsEditable(true);
      
      // Also clear selected node in the store if needed
      useFlowStore.getState().selectedNode = null;
    };
  }, []); // Empty dependency array means this runs only on unmount

  // Constants
  const youtubeRegex = /^(?:https?:\/\/)?(?:www\.)?(?:m\.)?(?:youtube\.com\/(?:watch\?v=[A-Za-z0-9_-]{11}(?:[&#?][^\s]*)?|shorts\/[A-Za-z0-9_-]{11}(?:[&#?][^\s]*)?|clip\/[A-Za-z0-9_-]+(?:[&#?][^\s]*)?)|youtu\.be\/[A-Za-z0-9_-]{11}(?:[&#?][^\s]*)?)$/i;

  const youtubeClipRegex = /^https?:\/\/(www\.)?youtube\.com\/clip\/[a-zA-Z0-9_-]+/;
  // reputation is now dynamically set from userFullDetails

  // First useEffect - fetch node data
  useEffect(() => {
    // Set loading state to true when navigating to a new page
    setIsLoading(true);

    // Reset state when loading a new node
    setVideos([]);
    setTitle('');
    setDescription('');
    setIsFlagged(false);
    setCreatedBy({ id: "", username: "" });
    setEditedBy([]);
    setIsEditing(false);
    setInputValue('');
    setEditTime("");
    setIsEditable(true);

    // Fetch the node data
    getNode({nodeId: nid!, tid: topic!});
  }, [getNode, nid, topic]);

  // Handle data loading and setup when selectedNode changes
  useEffect(() => {
    if (!selectedNode) return;

    // Set local state from selectedNode
    setTitle(selectedNode.title || '');
    setDescription(selectedNode.description || '');
    setIsFlagged(selectedNode.isFlagged || false);
    setCreatedBy(selectedNode.createdBy || { id: "", username: "" });
    setEditedBy(selectedNode.editedBy || []);

    // Check if node is empty (no title or description) and start in edit mode
    if (!selectedNode.title && !selectedNode.description) {
      setIsEditing(true);
    }

    // Always set and sort videos when selectedNode changes
    if (selectedNode.youtubeLinks) {
      const sortedVideos = [...selectedNode.youtubeLinks].sort((a, b) =>
        (b.votes || 0) - (a.votes || 0)
      );

      const videoMover = (array: LinkData[]): LinkData[] => {
        if (!array || array.length <= 3) return array;

        // Keep the top video
        const topVideo = array[0];

        // Get remaining videos
        const remainingVideos = array.slice(1);

        // Randomly select two videos from the remaining ones
        const randomIndex1 = Math.floor(Math.random() * remainingVideos.length);
        const firstPick = remainingVideos.splice(randomIndex1, 1)[0];

        const randomIndex2 = Math.floor(Math.random() * remainingVideos.length);
        const secondPick = remainingVideos.splice(randomIndex2, 1)[0];

        // Sort the remaining videos by votes
        const sortedRemaining = remainingVideos.sort((a, b) =>
          (b.votes || 0) - (a.votes || 0)
        );

        // Combine all parts
        return [
          topVideo,           // Position 0: Always top video
          firstPick,         // Position 1: Random pick
          secondPick,        // Position 2: Random pick
          ...sortedRemaining // Position 3+: Sorted by votes
        ];
      };

      const movedVideos = videoMover(sortedVideos);
      setVideos(movedVideos);
    } else {
      // If there are no YouTube links, ensure videos array is empty
      setVideos([]);
    }

    setIsLoading(false);
  }, [selectedNode]);

  // Update only vote counts when they change
  useEffect(() => {
    if (!selectedNode?.youtubeLinks || videos.length === 0) return;

    setVideos(prevVideos =>
      prevVideos.map(video => {
        const updatedVideo = selectedNode.youtubeLinks?.find(v => v.link === video.link);
        return updatedVideo ? { ...video, votes: updatedVideo.votes } : video;
      })
    );
  }, [selectedNode?.youtubeLinks, videos.length]);

  // Add this useEffect to load user details when the component mounts
  useEffect(() => {
    if (userDetails?.id) {
      getUser(userDetails.id);
    }
  }, [userDetails?.id, getUser]);

  // Third useEffect - for timer logic only (video moving logic moved to selectedNode effect)
  useEffect(() => {
    // Always set isEditable to true for admins or users with editor reputation
    // Use explicit number comparison for role
    const isAdmin = Number(userDetails?.role) === Number(KeyAdmin);
    const hasEditorReputation = Number(userDetails?.reputation ?? 0) >= Number(KeyReputationEditor);
    
    if (isAdmin || hasEditorReputation) {
      setIsEditable(true);
      setEditTime("");
      return; // Exit early for admins and editors
    }

    // Timer logic for regular users
    if (nid) {
      const creationTime = parseISO(nid);
      const futureTime = addMinutes(creationTime, 15);
      let intervalId: NodeJS.Timeout;

      // Check if current user is the creator of the node
      const isCreator = userDetails?.id === localCreatedBy?.id;
      const withinTimeWindow = isBefore(new Date(), futureTime);

      if (isCreator && withinTimeWindow) {
        // Only the creator can edit within the time window
        intervalId = setInterval(() => {
          const timeLeft = differenceInMilliseconds(futureTime, new Date());
          const formattedTime = format(timeLeft, 'mm:ss');

          if (timeLeft <= 0) {
            clearInterval(intervalId);
            setEditTime("");
            setIsEditable(false); // Creator can't edit after time window
            console.log("Time window expired - can no longer edit");
          } else {
            setEditTime(formattedTime);
            setIsEditable(true); // Creator can edit during the time window
          }
        }, 1000);
      } else {
        // Not the creator or outside time window
        setIsEditable(false);
      }

      return () => {
        if (intervalId) clearInterval(intervalId);
      };
    }
  }, [nid, userDetails?.reputation, userDetails?.role, userDetails?.id, localCreatedBy?.id]);

  // Loading check after all useEffect hooks
  if (isLoading) {
    return <div>Loading...</div>;
  }

  const errorDisplayer = (content: string | React.ReactNode) => {
    messageApi.open({
      type: 'error',
      content: content,
      duration: 2.5,
      style: { marginTop: '50px' }, // Add spacing between messages
      className: 'custom-error-message' // Add a custom class for styling
    })
    .then(() => clearError())
  };

  const extractVideoId = (url: string) => {
    try {
      const urlObj = new URL(url);
      const host = urlObj.hostname.replace(/^www\./, ""); // normalize

      if (host === 'youtube.com' || host === 'm.youtube.com') {
        if (urlObj.pathname === '/watch') {
          // Standard YouTube video
          return urlObj.searchParams.get('v');
        } else if (urlObj.pathname.startsWith('/shorts/')) {
          // YouTube Shorts
          const parts = urlObj.pathname.split('/');
          return parts.length > 2 ? parts[2] : null;
        } else if (urlObj.pathname.startsWith('/embed/')) {
          // Embedded player links
          const parts = urlObj.pathname.split('/');
          return parts.length > 2 ? parts[2] : null;
        }
      } else if (host === 'youtu.be') {
        // Shortened URL
        return urlObj.pathname.slice(1);
      }

      return null; // Not a valid YouTube video URL
    } catch (error) {
      console.error('Invalid URL:', error);
      return null;
    }
  };

  const areVideosSame = (url1: string, url2: string) => {
    const videoId1 = extractVideoId(url1);
    const videoId2 = extractVideoId(url2);

    // Compare the extracted video IDs
    return videoId1 !== null && videoId2 !== null && videoId1 === videoId2;
  };

  const handleBackClick = () => {
    // Check if the last map is set
    if (lastMap) {
      //go to the last map
      navigate(lastMap);
    } else {
      // If no last map, navigate to the map page
      navigate(`/${topic}/map`);
    }
  };

  const onClickEdit = async () => {
    if (isEditing && hasChanged()) {
      if (localTitle !== "" && localDescription !== "") {
        const updatedNode: NodeData = {
          title: localTitle,
          description: localDescription,
          id: nid!,
          topic: topic!
        }
        const [success, editorAdded] = await putNodeTitle(updatedNode);
        if (success && editorAdded) {
          if (Array.isArray(localEditedBy)) {
            if (!localEditedBy.some(editor => editor.id === userDetails!.id)) {
              setEditedBy([...localEditedBy, {
                id: userDetails!.id,
                username: userDetails!.username
              }]);
            }
          } else {
            setEditedBy([{
              id: userDetails!.id,
              username: userDetails!.username
            }]);
          }
        }
      } else {
        errorDisplayer("Title and description cannot be empty");
        setTitle(selectedNode?.title || '')
        setDescription(selectedNode?.description || '')
      }
    }
    setIsEditing(!isEditing)
  }

  const hasChanged = (): boolean => {
    return localTitle !== selectedNode?.title || localDescription !== selectedNode?.description
  }

  const onClickVideoUp = async (videoDetail: LinkData) => {
    const updatedNode: NodeData = {
      youtubeLinks: [{...videoDetail, votes: 1}],
      id: nid!,
      topic: topic!
    }
    await putNodeVideoVote(updatedNode)
  }

  const onClickVideoDown = async (videoDetail: LinkData) => {
    const updatedNode: NodeData = {
      youtubeLinks: [{...videoDetail, votes: -1}],
      id: nid!,
      topic: topic!
    }
    await putNodeVideoVote(updatedNode)
  }

  const onClickVideoDelete = async (link: string) => {
    const videoDetail = videos.find(item => item?.link === link);
    
    if (videoDetail) {
      const videoCopy = {...videoDetail}
      videoCopy.votes = -1
      const updatedNode: NodeData = {
        youtubeLinks: [{...videoCopy}],
        id: nid!,
        topic: topic!
      }
      const success = await putNodeVideoEdit(updatedNode);
      if (success) {
        // Update local state by removing the deleted video
        setVideos(prevVideos => prevVideos.filter(video => video.link !== link));

        // If we deleted the main video (first in the array), promote the next video to main
        if (videos[0]?.link === link && videos.length > 1) {
          setVideos(prevVideos => {
            const newMainVideo = prevVideos.length > 1 ? prevVideos[1] : null;
            const remainingVideos = prevVideos.filter(v => v.link !== link && v.link !== newMainVideo?.link);
            return newMainVideo ? [newMainVideo, ...remainingVideos] : remainingVideos;
          });
        }
      }
    } else {
      errorDisplayer(`Video with link ${link} not found.`);
    }
  }

  const onClickNodeDelete = async() => {
    const success = await deleteNode({nodeId: nid!, tid: topic!})
    if (success) {
      const currentUrl = new URL(window.location.href);
      const baseUrl = `${currentUrl.protocol}//${currentUrl.host}`;
      // This line is changed to navigate to the base URL plus the topic
      window.location.href = `${baseUrl}/${topic}/map`;
    } else {
      errorDisplayer("Failed to delete node");
    }
  }

  const userLink = (user: UserIdentifier): React.JSX.Element => {
    return (
      <Link
        to={`/user-page/${user.id}`}
        onClick={(e) => e.stopPropagation()} // Stop propagation to prevent parent click events
      >
        {user.username}
      </Link>
    );
  };

  const displayEditors = (
    editedBy: UserIdentifier[]
  ): React.JSX.Element[] => {
    return editedBy.map((editor, index) => (
      <React.Fragment key={editor.id}>
        {userLink(editor)}
        {index < editedBy.length - 1 && ', '}
      </React.Fragment>
    ));
  }

  const onClickFlag = async () => {
    //need to add only high level user and origional flagger can unflag
    const updatedNode: NodeData = {
      isFlagged: !localIsFlagged,
      id: nid!,
      topic: topic!
    }
    const success = await putNodeFlag(updatedNode)
    if (success) {
      setIsFlagged(!localIsFlagged)
    }
  }

  const onClickBattleUp = async () => {
    const updatedNode: NodeData = {
      battleTested: 1,
      id: nid!,
      topic: topic!
    }
    await putNodeBattleVote(updatedNode)
    // Remove setBattle as it will be handled by the store update
  }

  const onClickBattleDown = async () => {
    const updatedNode: NodeData = {
      battleTested: -1,
      id: nid!,
      topic: topic!
    }
    await putNodeBattleVote(updatedNode)
    // Remove setBattle as it will be handled by the store update
  }

  const onClickFreshUp = async () => {
    const updatedNode: NodeData = {
      fresh: 1,
      id: nid!,
      topic: topic!
    }
    await putNodeFreshVote(updatedNode)
    // Remove setFresh as it will be handled by the store update
  }

  const onClickFreshDown = async () => {
    const updatedNode: NodeData = {
      fresh: -1,
      id: nid!,
      topic: topic!
    }
    await putNodeFreshVote(updatedNode)
    // Remove setFresh as it will be handled by the store update
  }

  const onClickAddVideo = async () => {
    if (inputValue) {
      if (!youtubeRegex.test(inputValue)) {
        errorDisplayer("That is not a youtube link");
      }
      else if (videos.some(video => areVideosSame(video.link!, inputValue))) {
        errorDisplayer("That video is already in the list");
      }
      else {

        const addedByUser: UserIdentifier = {
          id: userDetails?.id || "",
          username: userDetails?.username || ""
        };

        let videoLink: string | null = null;
        // Check if the link is a clip and store the full URL.
        if (youtubeClipRegex.test(inputValue)) {
          videoLink = inputValue;
        } else {
          // For all other types, just store the video ID.
          videoLink = extractVideoId(inputValue);
        }

        if (!videoLink) {
          errorDisplayer("Could not extract a valid video ID from the link.");
          return;
        }

        const newVideo: LinkData = {
          link: videoLink,
          votes: 1,
          addedBy: addedByUser,
          dateAdded: format(new Date(), 'yyyy-MM-dd\'T\'HH:mm:ss.SSSXXX')
        };

        const updatedNode: NodeData = {
          youtubeLinks: [newVideo],
          id: nid!,
          topic: topic!
        };

        const success = await putNodeVideoEdit(updatedNode);
        if (success) {
          newVideo.votes = 0;

          if (videos.length === 0) {
            setVideos([newVideo]);
          } else {
            setVideos(prevVideos => [
              prevVideos[0],
              newVideo,
              ...prevVideos.slice(1)
            ]);
          }

          setInputValue("");
        }
      }
    }
  }

  const handleBattleTested = async () => {
    try {
      const nextNodeId = await getNextNodeBattleTested({ nodeId: nid!, tid: topic! });
      if (nextNodeId !== "") {
        navigate(`/${topic}/wiki-page/${nextNodeId}`);
      }
      else {
        errorDisplayer("No next node found");
      }
    } catch (err) {
      console.error("Failed to fetch next node:", err);
    }
  };

  const handleNextNodeFresh = async () => {
    try {
      const nextNodeId = await getNextNodeFresh({ nodeId: nid!, tid: topic! });
      if (nextNodeId !== "") {
        navigate(`/${topic}/wiki-page/${nextNodeId}`);
      }
      else {
        errorDisplayer("No next node found");
      }
    } catch (err) {
      console.error("Failed to fetch next node:", err);
    }
  };

  return (
    <AuthenticatedLayout>
      <div className="app-header shrink-height roots">
        <Button type="primary" onClick={handleBattleTested}>Battle Tested</Button>
        <h1>NEXT UP</h1>
        <Button onClick={handleNextNodeFresh} type="primary" danger>Fresh</Button>
      </div>
      <Card
        title={
          <Row>
            {isEditing ?
              <Input
                defaultValue={localTitle}
                onChange={handleTitleChange}
              />
              :
              <b style={{ paddingRight: '15px' }}>{localTitle}</b>
            }
            { !isEditing ?

              localIsFlagged ?
                <FlagFilled className='pop-button' onClick={onClickFlag} />
                :
                <FlagOutlined className='pop-button' onClick={onClickFlag} />
              :
              null
            }
            {isEditing ?
              <Popconfirm
                title="Delete the Node"
                description="Are you sure you want to delete this entire node?"
                onConfirm={onClickNodeDelete}
                icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
              >
                <Button type="primary" danger>Delete Node</Button>
              </Popconfirm>
              :
              null
            }
          </Row>
        }
        extra={
          <div>
            {isEditable && (
              <Button type="primary" onClick={onClickEdit}>{isEditing ? 'Save' : 'Edit'}</Button>
            )}
          </div>
        }
      >
        {contextHolder}
        <Flex align="flex-start" vertical justify="center">
          <Row className="fill-container">
            <Col span={16}>
              {isEditing ?
                <textarea
                  defaultValue={localDescription}
                  onChange={handleDescriptionChange}
                  rows={10}
                  style={{ width: '100%' }}
                />
                :
                <p>{localDescription}</p>
              }
              {!isEditing ?
                <>
                  {editTime === "" ?
                    null
                    :
                    <h1>You have {editTime} left to edit this node</h1>
                  }
                  <Row gutter={[8, 8]} align="middle" justify="start">
                    <Col xs={12} sm={3}>
                      <b>Battle Tested:</b>
                    </Col>
                    <Col xs={12} sm={21}>
                      <Space>
                        <UpCircleOutlined className="pop-button" onClick={onClickBattleUp} />
                        <p>{selectedNode?.battleTested || 0}</p>
                        <DownCircleOutlined className="pop-button" onClick={onClickBattleDown} />
                      </Space>
                    </Col>
                  </Row>

                  <Row gutter={[8, 8]} align={'middle'} justify={'start'}>
                    <Col xs={12} sm={3}>
                      <b>Fresh:</b>
                    </Col>
                    <Col xs={12} sm={21}>
                      <Space>
                        <UpCircleOutlined className='pop-button' onClick={onClickFreshUp} />
                        <p>{selectedNode?.fresh || 0}</p>
                        <DownCircleOutlined className='pop-button' onClick={onClickFreshDown} />
                      </Space>
                    </Col>

                  </Row>
                  <p><b>Created By:</b> {userLink(localCreatedBy)}</p> {/*sometimes currentUser*/}
                  {localEditedBy && <p><b>Edited By:</b> {displayEditors(localEditedBy)}</p>}
                  <Row style={{ marginTop: '16px' }}>
                    <Button onClick={handleBackClick}>Back to Map</Button>
                  </Row>
                </>
                :
                null
              }
            </Col>
            <Col
              xs={24}
              sm={24}
              md={24}
              lg={24}
              xl={8}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}
            >
              <div style={{
                marginBottom: '10px',
                textAlign: 'center',
                width: '100%',
                display: 'flex',
                flexDirection: screens.xl ? 'row' : 'column',
                justifyContent: 'center',
                gap: '10px'
              }}>
                {videos.length > 0 ? (
                  <>
                    <span>Added By: {userLink(videos[0]?.addedBy || { id: "", username: "" })}</span>
                    <span>
                      {videos[0]?.dateAdded && "Date Added: " + format(parseISO(videos[0].dateAdded), 'MM/dd/yyyy')}
                    </span>
                  </>
                ) : (
                  <span>Default Tutorial Video</span>
                )}
              </div>
              {isEditing && videos.length > 0 && (
                <Button
                  danger
                  style={{ marginBottom: '10px' }}
                  onClick={() => onClickVideoDelete(videos[0].link!)}
                >
                  Delete
                </Button>
              )}
              {videos.length > 0 && (
                <>
                <PlayerPage item={videos[0].link!} />
                <Row justify="center" align="middle" style={{ marginTop: '10px', width: '100%' }}>
                  <Space size="large">
                    <UpCircleOutlined className='pop-button' onClick={() => onClickVideoUp(videos[0])} />
                    <span style={{ margin: '0 10px' }}>{videos[0]?.votes ?? 0}</span>
                    <DownCircleOutlined className='pop-button' onClick={() => onClickVideoDown(videos[0])} />
                  </Space>
                </Row>
                </>
              )}
            </Col>
          </Row>

          <div>
            <Row align={'middle'} justify='space-between'>
              <Col span={18}>
                <Space.Compact style={{ width: '100%' }}>
                  { isEditing ?
                    null
                    :
                    <>
                      <Input
                        value={inputValue}
                        onChange={(e) => {
                          setInputValue(e.target.value);
                        }}
                        placeholder="Youtube Link With Time"
                      />
                      <Button type="primary" onClick={onClickAddVideo}>Add Link</Button>
                    </>
                  }
                </Space.Compact>
              </Col>
            </Row>
          </div>
        <div style={{ width: '100%', padding: '16px' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
              gap: '24px',
              padding: '16px',
              border: '1px solid #f0f0f0',
              borderRadius: '2px',
              minHeight: '200px'
            }}
          >
            {videos?.slice(1).map((item, index) => (
              <VideoCard
                key={item?.link || index}
                item={item}
                onClickVideoUp={onClickVideoUp}
                onClickVideoDown={onClickVideoDown}
                onClickVideoDelete={onClickVideoDelete}
                userLink={userLink}
                isEditing={isEditing}
              />
            ))}
          </div>
          {videos?.slice(1).length > 0 && (
            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center' }}>
              <Pagination
                size="small"
                total={videos?.slice(1).length}
                pageSize={12}
                showSizeChanger={false}
              />
            </div>
          )}
        </div>
        </Flex>
      </Card>
    </AuthenticatedLayout>

  )
}

export default WikiPage
